import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { styled, css } from 'styled-components';
import { Checkbox } from '@fabric-app/ui/components/Checkbox/Checkbox';
import { useMutationTagDelete } from '../mutations/useMutationTagDelete.js';
import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { IconMobileSheet24MiscEdit, IconMobileSheet24MiscDelete } from '@fabric-app/icons/svg';
import { tagRenameModal } from './ModalRenameTag/tagRenameStore.js';
import { cssTextEllipsis } from '@fabric-app/ui/constants/styled-utils';
import { Link } from '@fabric-app/compat';

const TagBadge = ({ size, tag, count, onSelect, disableEdit }) => {
  const htmlId = `tag-${tag.id}-select`;
  const isSelectable = !!onSelect;
  const wrapperProps = isSelectable ? {
    as: "label",
    htmlFor: htmlId
  } : {};
  return /* @__PURE__ */ jsxs(TagBadgeWrapper, {
    size,
    ...wrapperProps,
    children: [
      !!onSelect || !!tag.isDraft ? null : /* @__PURE__ */ jsx(OverlappingLink, {
        href: `/tags/${tag.id}`
      }),
      isSelectable && /* @__PURE__ */ jsx(Fragment, {
        children: /* @__PURE__ */ jsx(StyledCheckbox, {
          id: htmlId,
          name: htmlId,
          "aria-label": `Select tag ${tag.name}`,
          checked: tag.checked,
          onCheckedChange: () => {
            onSelect?.(tag, !tag.checked);
          }
        })
      }),
      /* @__PURE__ */ jsx(TagName, {
        children: tag.name
      }),
      typeof count === "number" && /* @__PURE__ */ jsx(CountBadge, {
        children: count
      }),
      !disableEdit && /* @__PURE__ */ jsx(TagItemDropdown, {
        tag
      })
    ]
  });
};
const TagItemDropdown = ({ tag }) => {
  const deleteTagMutation = useMutationTagDelete();
  return /* @__PURE__ */ jsx(Fragment, {
    children: /* @__PURE__ */ jsxs(DropdownMenu.Base, {
      renderTriggerElement: ({ onOpenChange }) => /* @__PURE__ */ jsx(StyledDropdownMenuTriggerButtonThreeDots, {
        variant: "transparent",
        onOpenChange
      }),
      contentProps: {
        side: "bottom",
        align: "center"
      },
      children: [
        /* @__PURE__ */ jsx(DropdownMenu.Group, {
          children: /* @__PURE__ */ jsx(DropdownMenu.Item, {
            iconElement: /* @__PURE__ */ jsx(IconMobileSheet24MiscEdit, {}),
            onClick: () => {
              tagRenameModal.open({
                tag
              });
            },
            children: "Edit tag name"
          })
        }),
        /* @__PURE__ */ jsx(DropdownMenu.Group, {
          children: /* @__PURE__ */ jsx(DropdownMenu.Item, {
            variant: "danger",
            iconElement: /* @__PURE__ */ jsx(IconMobileSheet24MiscDelete, {}),
            onClick: () => {
              deleteTagMutation.confirmAndMutate(tag.id);
            },
            children: "Delete tag"
          })
        })
      ]
    })
  });
};
const StyledDropdownMenuTriggerButtonThreeDots = styled(DropdownMenu.TriggerButtonThreeDots).withConfig({
  displayName: "TagBadge__StyledDropdownMenuTriggerButtonThreeDots",
  componentId: "sc-cb6c9034-0"
})([
  `margin-right:-13px;border-radius:11px;color:rgba(`,
  `,0.4);`
], cssVar["color-text-primary-rgb"]);
const SIZE_PROPERTIES = {
  "44": css([
    `height:2.75rem;font-size:0.9375rem;padding:0 0.9375rem;border-radius:0.75rem;`
  ]),
  "32": css([
    `height:2rem;font-size:0.875rem;padding:0 0.5rem;border-radius:0.5rem;`
  ])
};
const TagBadgeWrapper = styled.div.withConfig({
  displayName: "TagBadge__TagBadgeWrapper",
  componentId: "sc-cb6c9034-1"
})([
  `position:relative;display:flex;width:max-content;align-items:center;gap:0.5rem;cursor:pointer;padding:0 0.9375rem;`,
  `;background-color:rgba(`,
  `,0.015);&:hover{box-shadow:0px 0px 1000px 0px rgba(`,
  `,0.04) inset;}`
], ({ size }) => SIZE_PROPERTIES[size || "44"], cssVar["color-bg-primary-reverse-rgb"], cssVar["color-bg-primary-reverse-rgb"]);
const CountBadge = styled.div.withConfig({
  displayName: "TagBadge__CountBadge",
  componentId: "sc-cb6c9034-2"
})([
  `display:flex;align-items:center;justify-content:center;background-color:rgba(`,
  `,0.05);padding:2px 3px;font-weight:600;font-size:0.75rem;line-height:15px;border-radius:6px;color:rgba(`,
  `,0.5);`
], cssVar["color-bg-primary-reverse-rgb"], cssVar["color-text-primary-rgb"]);
const StyledCheckbox = styled(Checkbox.Default).withConfig({
  displayName: "TagBadge__StyledCheckbox",
  componentId: "sc-cb6c9034-3"
})([
  `transition-property:color,background-color,border-color,text-decoration-color,fill,stroke;transition-duration:0.2s;transition-timing-function:ease-in-out;&[aria-checked='false']{background:`,
  `;border-color:`,
  `;}`
], cssVar["color-bg-primary"], cssVar["color-border-secondary"]);
const TagName = styled.span.withConfig({
  displayName: "TagBadge__TagName",
  componentId: "sc-cb6c9034-4"
})([
  `white-space:nowrap;`,
  `;`
], cssTextEllipsis);
const OverlappingLink = styled(Link).withConfig({
  displayName: "TagBadge__OverlappingLink",
  componentId: "sc-cb6c9034-5"
})([
  `position:absolute;inset:0;`
]);

export { TagBadge };
//# sourceMappingURL=TagBadge.js.map
